import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailField } from '@/components/ui/entity-detail-field';
import { useMemo } from 'react';
import {
	EntityDetailsHeader,
	type EntityDetailsHeaderActions
} from '@/components/ui/entity-details-header';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import { StatusBadge } from '@/components/ui/status-badge';
import type { LimitPolicyQuery } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { FileText, History, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { LIMIT_POLICY_QUERY } from './limit-policies.gql.tsx';

type LimitPolicyDetailsData = NonNullable<LimitPolicyQuery['limitPolicy']>;

interface LimitPolicyDetailsProps {
	limitPolicyId: string;
	// Header props for modal / detached view display
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

const LimitPolicyEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Shield className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const LimitPolicyDetailsHeader = ({
	policy,
	className,
	...actions
}: {
	policy: Pick<LimitPolicyDetailsData, 'limitPolicyId' | 'name' | 'isActive'>;
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t: tLP } = useTranslation('limitPolicies');
	const entityTypeName = policy.isActive
		? tLP('details.activePolicy')
		: tLP('details.inactivePolicy');

	return (
		<EntityDetailsHeader
			icon={<LimitPolicyEntityIcon />}
			name={policy.name}
			statusBadge={<StatusBadge isActive={policy.isActive} />}
			entityTypeName={entityTypeName}
			entityId={actions.entityId ?? policy.limitPolicyId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			className={className}
		/>
	);
};

export function LimitPolicyDetails({
	limitPolicyId,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: LimitPolicyDetailsProps) {
	const { t } = useTranslation('common');
	const { t: tLP } = useTranslation('limitPolicies');
	const { data, isLoading, isError } = useOne<LimitPolicyDetailsData>({
		resource: 'limitPolicies',
		id: limitPolicyId,
		meta: {
			gqlQuery: LIMIT_POLICY_QUERY,
			dataPath: 'limitPolicy'
		}
	});

	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 100,
		showInitialLoading: true
	});

	const limitPolicy = data?.data;

	const limitSummary = useMemo(() => {
		if (!limitPolicy?.limit) {
			return null;
		}

		const { minAmount, maxAmount, minStayMinutes, maxStayMinutes } = limitPolicy.limit;

		const formatCurrency = (value: number | null | undefined) => {
			if (value == null) return tLP('details.notSet');
			return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(value);
		};

		const formatDuration = (value: number | null | undefined) => {
			if (value == null) return tLP('details.notSet');
			const hours = Math.floor(value / 60);
			const minutes = value % 60;
			if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
			if (hours > 0) return `${hours}h`;
			return `${minutes}m`;
		};

		return {
			minAmount: formatCurrency(minAmount),
			maxAmount: formatCurrency(maxAmount),
			minStayMinutes: formatDuration(minStayMinutes),
			maxStayMinutes: formatDuration(maxStayMinutes)
		};
	}, [limitPolicy?.limit, tLP]);

	if (shouldShowLoading) {
		return (
			<div className="space-y-6">
				<div className="space-y-2">
					<div className="w-40 h-6 rounded bg-muted animate-pulse" />
					<div className="w-32 h-4 rounded bg-muted animate-pulse" />
					<div className="w-24 h-4 rounded bg-muted animate-pulse" />
				</div>
				<div className="h-32 rounded-md bg-muted animate-pulse" />
				<div className="h-32 rounded-md bg-muted animate-pulse" />
				<div className="h-32 rounded-md bg-muted animate-pulse" />
			</div>
		);
	}

	if (!isLoading && (isError || !limitPolicy)) {
		return (
			<div className="py-8 text-sm text-center text-muted-foreground">
				{tLP('details.loadError')}
			</div>
		);
	}

	if (!limitPolicy) {
		return null;
	}

	return (
		<div className="flex flex-col h-full">
			{showHeader && (
				<LimitPolicyDetailsHeader
					policy={limitPolicy}
					entityId={limitPolicyId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4"
				/>
			)}

			<Tabs defaultValue="details" className="w-full flex-1">
				<TabsList>
					<TabLink value="details" icon={FileText} label={t('tabs.details')} />
					<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
				</TabsList>

				<TabsContent value="details" className="space-y-6 text-base mt-6">
					<DetailSection>
						<DetailFieldsGrid>
							<DetailField
								label={tLP('details.priority')}
								value={
									limitPolicy.priority?.toString() ??
									tLP('details.notSet')
								}
							/>
							<DetailField
								label={tLP('details.billingCycle')}
								value={
									limitPolicy.billingCycle?.name ??
									tLP('details.notSet')
								}
							/>
						</DetailFieldsGrid>
					</DetailSection>

					<DetailSection title={t('sections.scenario_assignment')}>
						<DetailFieldsGrid>
							<EntityDetailField
								label={tLP('details.scenario')}
								value={limitPolicy.scenario?.name}
								entityId={limitPolicy.scenario?.scenarioId}
								entityType="scenario"
							/>
						</DetailFieldsGrid>
					</DetailSection>

					{limitPolicy.limit && (
						limitPolicy.limit.minAmount != null ||
						limitPolicy.limit.maxAmount != null ||
						limitPolicy.limit.minStayMinutes != null ||
						limitPolicy.limit.maxStayMinutes != null
					) && (
						<DetailSection title={t('sections.limit_enforcement')}>
							<DetailFieldsGrid>
								{limitSummary?.minAmount && limitPolicy.limit.minAmount != null && (
									<DetailField
										label={tLP('details.minAmount')}
										value={limitSummary.minAmount}
									/>
								)}
								{limitSummary?.maxAmount && limitPolicy.limit.maxAmount != null && (
									<DetailField
										label={tLP('details.maxAmount')}
										value={limitSummary.maxAmount}
									/>
								)}
								{limitSummary?.minStayMinutes && limitPolicy.limit.minStayMinutes != null && (
									<DetailField
										label={tLP('details.minStayMinutes')}
										value={limitSummary.minStayMinutes}
									/>
								)}
								{limitSummary?.maxStayMinutes && limitPolicy.limit.maxStayMinutes != null && (
									<DetailField
										label={tLP('details.maxStayMinutes')}
										value={limitSummary.maxStayMinutes}
									/>
								)}
							</DetailFieldsGrid>
						</DetailSection>
					)}
				</TabsContent>

				<TabsContent value="audit" className="mt-6">
					<AuditTab
						data={{
							activatedAt: limitPolicy.activatedAt,
							deactivatedAt: limitPolicy.deactivatedAt
						}}
					/>
				</TabsContent>
			</Tabs>
		</div>
	);
}
